import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../Pages/Navbar/EmployerNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import Swal from 'sweetalert2';
import './EmployerMessages.css';

function EmployerMessages() {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;

    const [allMessages, setAllMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [replying, setReplying] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    // Chat config
    const [employerSubPlan, setEmployerSubPlan] = useState('free');
    const [allowFreeCustomChat, setAllowFreeCustomChat] = useState(false);
    const [chatStatus, setChatStatus] = useState({ stepCount: 0, isLocked: false, nextSenderIsCandidate: true });

    // Contact feature
    const [contactFeatureAccess, setContactFeatureAccess] = useState({ allowed: false, reason: 'no_subscription' });
    const [contactUnlockedIds, setContactUnlockedIds] = useState([]);
    const [contactActionLoading, setContactActionLoading] = useState(false);
    const [candidateHasSub, setCandidateHasSub] = useState(false);

    const isMobile = screenWidth < 768;

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch all messages and chat config
    useEffect(() => {
        if (!userId) return;

        const fetchAllData = async () => {
            setLoading(true);
            try {
                const [msgRes, subRes, adminRes] = await Promise.all([
                    fetch(`${BaseUrl}/message/conversations/employer/${userId}`),
                    fetch(`${BaseUrl}/api/employer/subscription/${userId}`),
                    fetch(`${BaseUrl}/api/admin/settings/allow_free_custom_chat`)
                ]);

                if (msgRes.ok) {
                    const data = await msgRes.json();
                    setAllMessages(data);
                }

                if (subRes.ok) {
                    const data = await subRes.json();
                    setEmployerSubPlan(data.subscription?.plan_type || 'free');
                }

                if (adminRes.ok) {
                    const data = await adminRes.json();
                    setAllowFreeCustomChat(data.is_enabled === 1);
                }
            } catch (err) {
                console.error('Error fetching messages:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();

        // Polling logic for real-time messages
        const pollInterval = setInterval(() => {
            fetch(`${BaseUrl}/message/conversations/employer/${userId}`)
                .then(res => {
                    if (res.ok) return res.json();
                    throw new Error('Network response was not ok');
                })
                .then(data => setAllMessages(data))
                .catch(err => console.error('Error polling messages:', err));
        }, 15000); // 15 seconds polling

        return () => clearInterval(pollInterval); // Cleanup on unmount
    }, [userId]);

    // Derive jobs from messages
    const jobsMap = {};
    allMessages.forEach(msg => {
        const mapKey = msg.job_id || 'direct_messages';
        if (!jobsMap[mapKey]) {
            jobsMap[mapKey] = {
                job_id: msg.job_id || null,
                is_direct: !msg.job_id,
                job_title: msg.job_title || (msg.job_id ? `Job #${msg.job_id}` : 'Direct Messages'),
                working_city: msg.working_city,
                country: msg.country,
                domestic_worker_category: msg.domestic_worker_category,
                messages: [],
                lastMessageTime: msg.created_at
            };
        }
        jobsMap[mapKey].messages.push(msg);
        // Track latest message time
        if (new Date(msg.created_at) > new Date(jobsMap[mapKey].lastMessageTime)) {
            jobsMap[mapKey].lastMessageTime = msg.created_at;
        }
    });

    const jobs = Object.values(jobsMap).sort(
        (a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
    );

    const getUnreadCountForJob = (job) => {
        if (!job?.messages?.length) return 0;
        return job.messages.filter((msg) => {
            const incoming = String(msg.sender_id) !== String(userId);
            const unread = !(Number(msg.is_read) === 1);
            return incoming && unread;
        }).length;
    };

    const totalUnreadCount = jobs.reduce((sum, job) => sum + getUnreadCountForJob(job), 0);

    // Candidates for selected job
    const getCandidatesForJob = (jobId, isDirect) => {
        const jobMsgs = isDirect ? allMessages.filter(m => !m.job_id) : allMessages.filter(m => m.job_id === jobId);
        const candidateMap = {};
        jobMsgs.forEach(m => {
            if (!candidateMap[m.job_seeker_id]) {
                candidateMap[m.job_seeker_id] = {
                    job_seeker_id: m.job_seeker_id,
                    first_name: m.first_name,
                    last_name: m.last_name,
                    profile_photo: m.profile_photo,
                    lastMessage: m
                };
            }
            if (new Date(m.created_at) > new Date(candidateMap[m.job_seeker_id].lastMessage.created_at || 0)) {
                candidateMap[m.job_seeker_id].lastMessage = m;
            }
        });
        return Object.values(candidateMap).sort((a,b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
    };

    const getUnreadCountForCandidate = (candidate, job) => {
        if (!candidate || !job) return 0;
        const conversationMessages = allMessages.filter((m) => {
            const sameCandidate = String(m.job_seeker_id) === String(candidate.job_seeker_id);
            const sameJob = job.is_direct ? !m.job_id : String(m.job_id) === String(job.job_id);
            return sameCandidate && sameJob;
        });
        return conversationMessages.filter((msg) => {
            const incoming = String(msg.sender_id) !== String(userId);
            const unread = !(Number(msg.is_read) === 1);
            return incoming && unread;
        }).length;
    };

    // Conversation for selected candidate in selected job
    const getConversation = () => {
        if (!selectedJob || !selectedCandidate) return [];
        if (selectedJob.is_direct) {
            return allMessages.filter(
                m => !m.job_id && m.job_seeker_id === selectedCandidate.job_seeker_id
            );
        }
        return allMessages.filter(
            m => m.job_id === selectedJob.job_id && m.job_seeker_id === selectedCandidate.job_seeker_id
        );
    };

    const fetchConversationStatus = async (jobId, jobSeekerId) => {
        try {
            const statusRes = await fetch(`${BaseUrl}/message/conversation-status?job_id=${jobId}&job_seeker_id=${jobSeekerId}`);
            if (statusRes.ok) {
                const statusData = await statusRes.json();
                setChatStatus(statusData);
            }
        } catch (e) {
            console.error('Error fetching chat status:', e);
        }
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
        setSelectedCandidate(null);
        setReplyText('');
    };

    const handleSelectCandidate = async (candidate) => {
        setSelectedCandidate(candidate);
        setReplyText('');
        setCandidateHasSub(false); // Reset
        await markConversationAsRead(candidate, selectedJob);
        if (selectedJob) {
            await fetchConversationStatus(selectedJob.job_id, candidate.job_seeker_id);
            // Fetch candidate subscription
            try {
                const subRes = await fetch(`${BaseUrl}/api/candidate/subscription/${candidate.job_seeker_id}`);
                if (subRes.ok) {
                    const subData = await subRes.json();
                    const sub = subData.subscription || {};
                    const planName = String(sub.plan_type || sub.plan_name || sub.name || '').toLowerCase();
                    const isPaid = planName !== '' && !planName.includes('free');
                    setCandidateHasSub(sub.status === 'active' && isPaid);
                }
            } catch (e) {
                console.error('Error fetching candidate sub:', e);
            }
        }
    };

    const markConversationAsRead = async (candidate, job) => {
        if (!candidate || !job || !userId) return;
        const unreadCount = getUnreadCountForCandidate(candidate, job);
        if (unreadCount <= 0) return;

        try {
            const res = await fetch(`${BaseUrl}/message/mark-read`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: userId,
                    job_seeker_id: candidate.job_seeker_id,
                    job_id: job.is_direct ? null : job.job_id
                })
            });

            if (!res.ok) return;

            setAllMessages(prev =>
                prev.map(msg => {
                    const sameCandidate = String(msg.job_seeker_id) === String(candidate.job_seeker_id);
                    const sameJob = job.is_direct
                        ? !msg.job_id
                        : String(msg.job_id) === String(job.job_id);
                    const incoming = String(msg.sender_id) !== String(userId);
                    if (sameCandidate && sameJob && incoming) {
                        return { ...msg, is_read: 1 };
                    }
                    return msg;
                })
            );
        } catch (err) {
            console.error('Error marking employer conversation as read:', err);
        }
    };

    useEffect(() => {
        if (!selectedCandidate || !selectedJob || allMessages.length === 0) return;
        markConversationAsRead(selectedCandidate, selectedJob);
    }, [allMessages, selectedCandidate, selectedJob]);

    const handleContactClick = (type) => {
        if (!selectedCandidate) return;

        // If employer is on free plan, block and show upgrade message
        if (employerSubPlan === 'free') {
            Swal.fire({
                title: 'Upgrade Required',
                text: 'You need an active subscription plan to view candidate contact details.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'View Plans',
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/subscriptionplans');
                }
            });
            return;
        }

        // If subscribed, get the numbers from the candidate's last message object
        const lastMsg = selectedCandidate.lastMessage;

        if (type === 'call') {
            const phoneCode = lastMsg.cand_phone_code || '';
            const phone = lastMsg.cand_phone || '';
            if (!phone) {
                Swal.fire('Not Available', 'Candidate has not provided a phone number.', 'info');
                return;
            }
            window.location.href = `tel:${phoneCode}${phone}`;

        } else if (type === 'whatsapp') {
            const waCode = lastMsg.cand_whatsapp_code || lastMsg.cand_phone_code || '';
            const waNum = lastMsg.cand_whatsapp || lastMsg.cand_phone || ''; // fallback to regular phone if WA missing
            if (!waNum) {
                Swal.fire('Not Available', 'Candidate has not provided a WhatsApp number.', 'info');
                return;
            }
            // Strip any non-numeric chars from the composite string for wa.me link
            const fullNum = `${waCode}${waNum}`.replace(/\D/g, '');
            window.open(`https://wa.me/${fullNum}`, '_blank');
        }
    };

    const refreshMessages = async () => {
        try {
            const res = await fetch(`${BaseUrl}/message/conversations/employer/${userId}`);
            if (res.ok) {
                const data = await res.json();
                setAllMessages(data);
            }
        } catch (err) {
            console.error('Error refreshing messages:', err);
        }
    };

    const handleReply = async () => {
        if (!replyText.trim() || !selectedCandidate || !selectedJob) return;

        const enforceGuided = employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub;
        if (enforceGuided && (chatStatus.nextSenderIsCandidate || chatStatus.isLocked)) {
            Swal.fire({
                html: `
                    <div style="text-align:center;padding:10px 0">
                        <div style="font-size:3rem;margin-bottom:12px">📱💬</div>
                        <p style="font-size:1rem;font-weight:600;color:#333;line-height:1.5">
                            ${chatStatus.isLocked
                        ? 'You have reached the maximum number of free messages. Subscribe to continue chatting freely.'
                        : 'Wait for the candidate to reply, or subscribe to contact them directly.'
                    }
                        </p>
                    </div>`,
                showCancelButton: true,
                confirmButtonText: '<i class="bi bi-whatsapp"></i> Subscribe Now',
                cancelButtonText: '<i class="bi bi-chat-left-dots"></i> Wait for Reply',
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#6c757d',
                reverseButtons: false,
                showCloseButton: true,
                customClass: {
                    popup: 'subscribe-intercept-popup'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/subscriptionplans');
                }
            });
            return;
        }

        setReplying(true);
        try {
            const res = await fetch(`${BaseUrl}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: userId,
                    job_seeker_id: selectedCandidate.job_seeker_id,
                    job_id: selectedJob.job_id,
                    sender_id: userId,
                    message: replyText,
                    sender_type: 'employer',
                    is_custom_chat: !(employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub)
                })
            });
            if (!res.ok) {
                let errorData = {};
                try {
                    errorData = await res.json();
                } catch (parseErr) {
                    errorData = {};
                }
                Swal.fire({ icon: 'error', title: 'Failed', text: errorData.error || 'Failed to send reply.' });
                return;
            }
            setReplyText('');
            await refreshMessages();
            await fetchConversationStatus(selectedJob.job_id, selectedCandidate.job_seeker_id);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send reply. Please try again.' });
        } finally {
            setReplying(false);
        }
    };

    const handleGuidedReply = async (stepCount) => {
        let msg = "";
        const candidateName = ((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate';
        if (stepCount === 1) msg = `Hello ${candidateName}, \nThank you for your interest.We have received your message.We will review your profile and contact you if you are shortlisted.`;
        if (stepCount === 3) msg = `Hello ${candidateName}, \nThank you for your patience.Your profile looks suitable.Please share your experience details, expected salary, and availability for interview.`;
        if (stepCount === 5) msg = `Thank you for the details.\nWe would like to schedule your interview.Please confirm your availability and preferred time.`;

        if (!msg) return;
        setReplying(true);
        try {
            const res = await fetch(`${BaseUrl}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: userId,
                    job_seeker_id: selectedCandidate.job_seeker_id,
                    job_id: selectedJob.job_id,
                    sender_id: userId,
                    message: msg,
                    sender_type: 'employer'
                })
            });
            if (!res.ok) throw new Error('Reply failed');
            await refreshMessages();
            await fetchConversationStatus(selectedJob.job_id, selectedCandidate.job_seeker_id);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send reply.' });
        } finally {
            setReplying(false);
        }
    };

    const conversation = getConversation();
    const candidates = selectedJob ? getCandidatesForJob(selectedJob.job_id, selectedJob.is_direct) : [];

    // Mobile view logic
    const showJobList = isMobile ? (!selectedJob) : true;
    const showCandidateList = isMobile ? (selectedJob && !selectedCandidate) : !!selectedJob;
    const showChat = isMobile ? (!!selectedCandidate) : !!selectedCandidate;

    return (
        <>
            <Navbar />
            <div className="employer-messages-page" style={{ marginTop: '80px' }}>
                <div className="employer-messages-container">
                    {loading ? (
                        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: '400px' }}>
                            <div className="spinner-border text-success" role="status" />
                            <span className="ms-3 text-muted">Loading messages...</span>
                        </div>
                    ) : allMessages.length === 0 ? (
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 text-muted" style={{ minHeight: '400px' }}>
                            <i className="bi bi-inbox" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                            <p className="mt-3">No messages yet. Messages will appear here when candidates message you about your jobs.</p>
                        </div>
                    ) : (
                        <div className="employer-messages-layout">
                            {/* Left — Job List */}
                            {showJobList && (
                                <div className="employer-messages-jobs-panel">
                                    <div className="panel-header">
                                        <i className="bi bi-chat-dots me-2 text-success"></i>
                                        <strong>Messages</strong>
                                        <span className="badge bg-success ms-2">{jobs.length}</span>
                                        {totalUnreadCount > 0 && (
                                            <span className="badge bg-danger ms-2">{totalUnreadCount}</span>
                                        )}
                                    </div>
                                    {jobs.map(job => {
                                        const isSelected = selectedJob?.job_id === job.job_id && selectedJob?.is_direct === job.is_direct;
                                        const msgCount = job.messages.length;
                                        const unreadCount = getUnreadCountForJob(job);
                                        return (
                                            <div
                                                key={job.is_direct ? 'direct_messages' : job.job_id}
                                                className={`job-item ${isSelected ? 'selected' : ''} ${unreadCount > 0 ? 'job-item-unread' : ''}`}
                                                onClick={() => handleSelectJob(job)}
                                            >
                                                <div className="job-item-avatar">
                                                    {job.is_direct ? (
                                                        <i className="bi bi-person-lines-fill fs-4"></i>
                                                    ) : (
                                                        <i className="bi bi-briefcase fs-5"></i>
                                                    )}
                                                </div>
                                                <div
                                                    className="job-item-content"
                                                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', minWidth: 0 }}
                                                >
                                                    <div className="job-item-title" style={{ textAlign: 'left', width: '100%' }}>{job.job_title}</div>
                                                    <div className="job-item-subtitle" style={{ textAlign: 'left', width: '100%' }}>
                                                        {job.is_direct ? (
                                                            <span>Candidates Without Job Link</span>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-geo-alt-fill me-1"></i>
                                                                {job.working_city || 'N/A'}, {job.country || 'N/A'}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="job-item-meta" style={{ justifyContent: 'flex-start', alignItems: 'flex-start', flexDirection: 'column', textAlign: 'left', width: '100%' }}>
                                                        <span className="badge bg-light text-dark">{msgCount} message{msgCount !== 1 ? 's' : ''}</span>
                                                        <small className="text-muted">
                                                            {new Date(job.lastMessageTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                        </small>
                                                    </div>
                                                    {unreadCount > 0 && (
                                                        <div className="message-unread-row">
                                                            <span className="message-unread-tag">Unread</span>
                                                            <span className="message-unread-count">{unreadCount}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Middle — Candidate List */}
                            {showCandidateList && (
                                <div className="employer-messages-candidates-panel">
                                    <div className="panel-header">
                                        {isMobile && (
                                            <button className="btn btn-link link-dark p-0 me-2" onClick={() => setSelectedJob(null)}>
                                                <i className="bi bi-arrow-left fs-5"></i>
                                            </button>
                                        )}
                                        <strong>{candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}</strong>
                                    </div>
                                    {candidates.length === 0 ? (
                                        <div className="text-center text-muted p-4">
                                            <i className="bi bi-inbox" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                                            <p className="mt-2">No messages for this job.</p>
                                        </div>
                                    ) : (
                                        candidates.map(c => {
                                            const isSelected = selectedCandidate?.job_seeker_id === c.job_seeker_id;
                                            const candidateName = ((c.first_name || '') + ' ' + (c.last_name || '')).trim() || `Candidate #${c.job_seeker_id}`;
                                            const lastMsg = c.lastMessage;
                                            const unreadCount = getUnreadCountForCandidate(c, selectedJob);
                                            return (
                                                <div
                                                    key={c.job_seeker_id}
                                                    className={`candidate-item ${isSelected ? 'selected' : ''} ${unreadCount > 0 ? 'candidate-item-unread' : ''}`}
                                                    onClick={() => handleSelectCandidate(c)}
                                                >
                                                    <div className="candidate-avatar">
                                                        {c.profile_photo ? (
                                                            <img src={c.profile_photo.includes('http') ? c.profile_photo : `${BaseUrl}${c.profile_photo}`} alt="P" />
                                                        ) : (
                                                            <i className="bi bi-person text-secondary"></i>
                                                        )}
                                                    </div>
                                                    <div className="candidate-info">
                                                        <div className="candidate-name" style={{ color: '#111111', fontWeight: 500 }}>{candidateName}</div>
                                                        {lastMsg && (
                                                            <div className="candidate-last-msg">
                                                                {lastMsg.sender_id === userId ? <><i className="bi bi-box-arrow-up-right me-1" style={{fontSize:'0.7rem',color:'#28a745'}}></i>You: </> : <i className="bi bi-box-arrow-in-down-left me-1" style={{fontSize:'0.7rem',color:'#6c757d'}}></i>}
                                                                {lastMsg.message?.substring(0, 45)}{lastMsg.message?.length > 45 ? '...' : ''}
                                                            </div>
                                                        )}
                                                        {unreadCount > 0 && (
                                                            <div className="message-unread-row">
                                                                <span className="message-unread-tag">Unread</span>
                                                                <span className="message-unread-count">{unreadCount}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}

                            {/* Right — Chat Thread */}
                            {showChat ? (
                                <div className="employer-messages-chat-panel">
                                    {/* Chat Header */}
                                    <div className="chat-header">
                                        <div className="d-flex align-items-center">
                                            {isMobile && (
                                                <button className="btn btn-link link-dark p-0 me-3" onClick={() => setSelectedCandidate(null)}>
                                                    <i className="bi bi-arrow-left fs-5"></i>
                                                </button>
                                            )}
                                            <div className="candidate-avatar me-2">
                                                {selectedCandidate?.profile_photo ? (
                                                    <img src={selectedCandidate.profile_photo.includes('http') ? selectedCandidate.profile_photo : `${BaseUrl}${selectedCandidate.profile_photo}`} alt="P" />
                                                ) : (
                                                    <i className="bi bi-person text-success"></i>
                                                )}
                                            </div>
                                            <span className="fw-bold">
                                                {((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || `Candidate #${selectedCandidate?.job_seeker_id}`}
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                onClick={() => handleContactClick('call')}
                                                title="Call Candidate"
                                            >
                                                <i className="bi bi-telephone-fill text-primary"></i>
                                            </button>
                                            <button
                                                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                onClick={() => handleContactClick('whatsapp')}
                                                title="WhatsApp Candidate"
                                            >
                                                <i className="bi bi-whatsapp text-success"></i>
                                            </button>
                                            <a
                                                href={`/allcandidatesdetails/${selectedCandidate?.job_seeker_id}`}
                                                target="_blank" rel="noreferrer"
                                                className="btn btn-outline-secondary btn-sm ms-2"
                                                style={{ fontSize: '0.72rem', padding: '1px 6px' }}
                                            >
                                                {isMobile ? 'Profile' : 'View Profile'}
                                            </a>
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div className="chat-messages">
                                        {conversation.map((msg, idx) => {
                                            const isFromMe = msg.sender_id === userId;
                                            const time = msg.created_at
                                                ? new Date(msg.created_at).toLocaleString('en-GB', {
                                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                                })
                                                : '';
                                            return (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    justifyContent: isFromMe ? 'flex-end' : 'flex-start'
                                                }}>
                                                    <div className={`chat-bubble ${isFromMe ? 'sent' : 'received'}`}>
                                                        <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                            {msg.message}
                                                        </div>
                                                        <div className="chat-time">
                                                            <i className={`bi ${isFromMe ? 'bi-box-arrow-up-right' : 'bi-box-arrow-in-down-left'} me-1`} style={{fontSize:'0.65rem'}}></i>{time}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Reply Box */}
                                    <div className="chat-reply-box">
                                        {(employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub) ? (
                                            <>
                                                {chatStatus.isLocked || chatStatus.nextSenderIsCandidate ? (
                                                    <div>
                                                        <div className="d-flex gap-2">
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows={1}
                                                                placeholder={chatStatus.isLocked ? 'Type a message...' : 'Type a message... (waiting for candidate reply)'}
                                                                value={replyText}
                                                                onChange={e => setReplyText(e.target.value)}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        handleReply();
                                                                    }
                                                                }}
                                                                disabled={replying}
                                                                style={{ resize: 'none', fontSize: '0.875rem', minHeight: '36px', height: '36px', maxHeight: '36px', lineHeight: '20px', padding: '7px 12px', boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'pre-wrap' }}
                                                            />
                                                            <button
                                                                className="btn btn-success btn-sm px-3"
                                                                onClick={handleReply}
                                                                disabled={replying || !replyText.trim()}
                                                                style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end', minHeight: '36px', height: '36px', maxHeight: '36px', paddingTop: '0', paddingBottom: '0' }}
                                                            >
                                                                {replying
                                                                    ? <span className="spinner-border spinner-border-sm" />
                                                                    : <i className="bi bi-send"></i>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex flex-column gap-2">
                                                        <div className="d-flex gap-2">
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows={1}
                                                                value={
                                                                    chatStatus.stepCount === 1
                                                                        ? `Hello ${((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate'}, Thank you for your interest. We have received your message. We will review your profile and contact you if you are shortlisted.`
                                                                        : chatStatus.stepCount === 3
                                                                            ? `Hello ${((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate'}, Thank you for your patience. Your profile looks suitable. Please share your experience details, expected salary, and availability for interview.`
                                                                            : "Thank you for the details. We would like to schedule your interview. Please confirm your availability and preferred time."
                                                                }
                                                                readOnly
                                                                style={{ resize: 'none', fontSize: '0.875rem', minHeight: '36px', height: '36px', maxHeight: '36px', lineHeight: '20px', padding: '7px 12px', boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'pre-wrap' }}
                                                            />
                                                            <button
                                                                className="btn btn-success btn-sm px-3"
                                                                onClick={() => handleGuidedReply(chatStatus.stepCount)}
                                                                disabled={replying}
                                                                style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end', minHeight: '36px', height: '36px', maxHeight: '36px', paddingTop: '0', paddingBottom: '0' }}
                                                            >
                                                                {replying
                                                                    ? <span className="spinner-border spinner-border-sm" />
                                                                    : <i className="bi bi-send"></i>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="d-flex flex-column gap-2">
                                                <div className="d-flex gap-2">
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={1}
                                                        placeholder="Type your reply..."
                                                        value={replyText}
                                                        onChange={e => setReplyText(e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                handleReply();
                                                            }
                                                        }}
                                                        disabled={replying}
                                                        style={{ resize: 'none', fontSize: '0.875rem', minHeight: '36px', height: '36px', maxHeight: '36px', lineHeight: '20px', padding: '7px 12px', boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'pre-wrap' }}
                                                    />
                                                    <button
                                                        className="btn btn-success btn-sm px-3"
                                                        onClick={handleReply}
                                                        disabled={replying || !replyText.trim()}
                                                        style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end', minHeight: '36px', height: '36px', maxHeight: '36px', paddingTop: '0', paddingBottom: '0' }}
                                                    >
                                                        {replying
                                                            ? <span className="spinner-border spinner-border-sm" />
                                                            : <i className="bi bi-send"></i>}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: '4px' }}>
                                            {isMobile ? 'Enter to send' : 'Press Enter to send, Shift+Enter for new line'}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                !isMobile && (
                                    <div className="employer-messages-chat-panel d-flex align-items-center justify-content-center text-muted">
                                        <div className="text-center">
                                            <i className="bi bi-chat-left-text" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                            <p className="mt-3">Select a candidate to view the conversation</p>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default EmployerMessages;
